import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlayIcon, StopIcon } from "./icons";
import { getMusicId, musicClass } from "./helpers";

// Styled bell-music dropdown. Options come from the API list (`items`); each can
// be auditioned inline. Preview playback is delegated to the shared
// useBellPreview controller passed in via `preview`, so it obeys the app-wide
// remote-first + /public/bells fallback and single-preview rules.
function MusicSelect({ items = [], value, t, onChange, preview, rowKey, disabled }) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const box = useRef(null);

    const selectedId = getMusicId(value);
    const selected = items.find((it) => getMusicId(it) === selectedId) || value || null;

    const optKey = (item) => `${rowKey}:opt:${getMusicId(item)}`;

    const halt = useCallback(() => preview.stop(), [preview]);

    // Close (and hush) on outside click.
    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => {
            if (box.current && !box.current.contains(e.target)) {
                setOpen(false);
                halt();
            }
        };
        document.addEventListener("mousedown", away);
        return () => document.removeEventListener("mousedown", away);
    }, [open, halt]);

    useEffect(() => {
        if (!open) halt();
    }, [open, halt]);

    const choose = (item) => {
        halt();
        setOpen(false);
        onChange && onChange(item);
    };

    const audition = (item, e) => {
        e.stopPropagation();
        preview.play(optKey(item), item?.url);
    };

    const onKey = (e) => {
        if (disabled) return;
        if (e.key === "Escape") {
            setOpen(false);
            halt();
            return;
        }
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                return;
            }
            if (!items.length) return;
            setActive((i) => (i + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length);
        }
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (open && items[active]) choose(items[active]);
            else setOpen(true);
        }
    };

    const label = selected?.label;

    return (
        <div className="dg-select" ref={box}>
            <button
                type="button"
                className="dg-select-btn"
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => {
                    setOpen((o) => !o);
                    setActive(Math.max(0, items.findIndex((it) => getMusicId(it) === selectedId)));
                }}
                onKeyDown={onKey}
            >
                <span className={"dg-select-label" + (label ? "" : " ph")}>
                    {label || t.pickMusic}
                </span>
                <span className="dg-chev" aria-hidden>▾</span>
            </button>

            {open && (
                <ul className="dg-menu" role="listbox" tabIndex={-1}>
                    {!items.length && <li className="dg-menu-empty">{t.loadingMusic}</li>}
                    {items.map((item, i) => {
                        const id = getMusicId(item);
                        const isPlaying = preview.playingKey === optKey(item);
                        return (
                            <li
                                key={id ?? i}
                                role="option"
                                aria-selected={id === selectedId}
                                className={
                                    "dg-opt" +
                                    (id === selectedId ? " sel" : "") +
                                    (active === i ? " act" : "") +
                                    (isPlaying ? " playing" : "")
                                }
                                onMouseEnter={() => setActive(i)}
                                onClick={() => choose(item)}
                            >
                                <span className={"dg-swatch " + musicClass(item)} aria-hidden />
                                <span className="dg-opt-name">{item.label}</span>
                                <button
                                    type="button"
                                    className={"dg-prev" + (isPlaying ? " on" : "")}
                                    title={isPlaying ? t.stop : t.listen}
                                    aria-label={isPlaying ? t.stop : t.listen}
                                    onClick={(e) => audition(item, e)}
                                >
                                    {isPlaying ? <StopIcon /> : <PlayIcon />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default MusicSelect;
